import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Button, Icon } from '@seeqdev/qomponents';
import { CopyButtonLocation, DateTimeEntry } from '@/datetime/DateTimeEntry.atom';
import { DurationEntry } from '@/core/DurationEntry.atom';
import { AutoUpdate } from '@/trend/AutoUpdate.molecule';
import type { RangeExport } from '@/trendData/duration.store';
import type { Timezone } from '@/datetime/timezone.service';
import { isPresentationWorkbookMode } from '@/utilities/utilities';
import {
  autoUpdate,
  displayRange as displayRangeDurationActions,
  investigateRange,
} from '@/trendData/duration.actions';
import { sqWorkbookStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';

export interface DisplayRangeSelectorProps {
  formMode?: boolean;
  autoUpdateDisabled?: boolean;
  rangeEditingEnabled?: boolean;
  durationEditingEnabled?: boolean;
  investigateRangeCopyDisabled?: boolean;
  investigateRangeCopyNonExistent?: boolean;
  timeZoneEditingEnabled?: boolean;
  label?: string;
  halfRangeButtonDisabled?: boolean;
  trackCategory?: string;
  trackAction?: string;
  inputExtraClassNames?: string;
  displayRange: RangeExport;
  isInTopic?: boolean;
  timezone: Timezone;
}

export const DisplayRangeSelector: React.FunctionComponent<DisplayRangeSelectorProps> = (props) => {
  const {
    label,
    formMode,
    autoUpdateDisabled,
    rangeEditingEnabled,
    durationEditingEnabled,
    timeZoneEditingEnabled = true,
    investigateRangeCopyDisabled,
    investigateRangeCopyNonExistent,
    halfRangeButtonDisabled,
    trackCategory,
    trackAction,
    inputExtraClassNames,
    displayRange,
    isInTopic = false,
    timezone,
  } = props;

  const { t } = useTranslation();

  const isPresentationMode = useFluxPath(sqWorkbookStore, (store) => isPresentationWorkbookMode(store.workbookDisplay));
  const displayRangeActions = displayRangeDurationActions;

  return (
    <div
      className={clsx('flexColumnContainer flexSpaceBetween displayRangeSelector mb2')}
      data-testid="displayRangeSelector">
      <div className="flexRowContainer">
        {formMode && (
          <label
            className={clsx(
              'font-size-smaller',
              { ml15: !investigateRangeCopyNonExistent },
              { disabled: !rangeEditingEnabled },
            )}>
            {t('START')}
          </label>
        )}
        <div className="flexColumnContainer">
          {!isPresentationMode && !investigateRangeCopyDisabled && (
            <span onClick={investigateRange.copyFromDisplayRange} className="copyToInvestigateIcon">
              <Icon
                icon="fc-arrow-down tw:text-sq-theme-dark"
                tooltip={t('RANGE_SELECTOR.COPY_TO_INVESTIGATE')}
                tooltipDelay={1000}
                tooltipPlacement="bottom"
                extraClassNames="displayRangeIcon  cursorPointer link-no-underline"
              />
            </span>
          )}

          <div
            id="displayRangeStart"
            className={clsx('displayRangeDateTime displayRangeStart', {
              'ml15': investigateRangeCopyNonExistent ? false : !isPresentationMode && investigateRangeCopyDisabled,
              'ml5': isPresentationMode,
              'min-width-145': !formMode && !isInTopic,
            })}>
            <DateTimeEntry
              extraClassName={clsx('small', {
                readOnly: !rangeEditingEnabled,
              })}
              testId="specDisplayRangeStart"
              date={displayRange.start}
              updateDate={(date) => setTimeout(() => displayRangeActions.updateStart(date!))}
              datePickerExtraClassNames="textAlignLeft fs11"
              // @ts-ignore updateBothDates expects the callback's option to be a string but `displayRangeActions.updateTimes` wants a PushOption.
              updateBothDates={displayRangeActions.updateTimes}
              timezone={timezone}
              otherDate={displayRange.end}
              readOnly={!rangeEditingEnabled}
              timeZoneReadOnly={!timeZoneEditingEnabled}
              trackCategory="'RangeSelector'"
              trackAction="'DateTimeEntry'"
              trackInformation="'Start Date set'"
              copyButtonLocation={CopyButtonLocation.Right}
            />
          </div>
        </div>
      </div>

      <div className="flexRowContainer">
        {label && <em>{label}</em>}
        {formMode && (
          <label className={clsx('flexSelfCenter font-size-smaller text-center mr50', { mr20: rangeEditingEnabled })}>
            {t('DURATION')}
          </label>
        )}

        <div
          className={clsx('flexColumnContainer tw:items-center', {
            flexCenter: formMode,
          })}
          id="displayRangeNavigation">
          {rangeEditingEnabled && (
            <Button
              onClick={displayRangeActions.stepBack}
              testId="rangeSelectorStepBackFull"
              icon="fc-step-back-full tw:text-sq-theme-dark"
              tooltip={t('RANGE_SELECTOR.STEP_BACK_FULL')}
              tooltipOptions={{ delay: 1000, position: 'bottom' }}
              size="xs"
              label=""
              variant="no-border"
              extraClassNames="displayRangeIcon"
            />
          )}
          {rangeEditingEnabled && !halfRangeButtonDisabled && (
            <Button
              onClick={displayRangeActions.stepBackHalf}
              testId="rangeSelectorStepBackHalf"
              extraClassNames="displayRangeIcon"
              size="xs"
              label=""
              variant="no-border"
              icon="fc-step-back-half tw:text-sq-theme-dark"
              tooltip={t('RANGE_SELECTOR.STEP_BACK_HALF')}
              tooltipOptions={{ delay: 1000, position: 'bottom' }}
            />
          )}

          {(rangeEditingEnabled || durationEditingEnabled) && (
            <DurationEntry
              extraClassNames={clsx({ 'min-width-50': formMode, 'min-width-75': !formMode })}
              inputExtraClassNames={clsx(inputExtraClassNames, 'text-center', {
                'min-width-40': formMode,
                'min-width-65': !formMode,
              })}
              startDate={displayRange.start}
              endDate={displayRange.end}
              updateDuration={displayRangeActions.updateDuration}
              updateDates={displayRangeActions.updateTimes}
              timezone={timezone}
              read-only={!rangeEditingEnabled && !durationEditingEnabled}
            />
          )}
          {rangeEditingEnabled && !halfRangeButtonDisabled && (
            <Button
              onClick={displayRangeActions.stepForwardHalf}
              testId="rangeSelectorStepForwardHalf"
              icon="fc-step-fore-half tw:text-sq-theme-dark"
              tooltip={t('RANGE_SELECTOR.STEP_FORE_HALF')}
              tooltipOptions={{ delay: 1000, position: 'bottom' }}
              label=""
              size="xs"
              variant="no-border"
            />
          )}
          {rangeEditingEnabled && (
            <>
              <Button
                onClick={displayRangeActions.stepForward}
                testId="rangeSelectorStepForward"
                label=""
                size="xs"
                variant="no-border"
                icon="fc-step-fore-full tw:text-sq-theme-dark"
                tooltip={t('RANGE_SELECTOR.STEP_FORE_FULL')}
                tooltipOptions={{ delay: 1000, position: 'bottom' }}
              />
              <Button
                onClick={displayRangeActions.stepToEnd}
                testId="rangeSelectorStepToEnd"
                icon="fc-arrow-right-right tw:text-sq-theme-dark"
                tooltip={t('RANGE_SELECTOR.STEP_TO_END')}
                tooltipOptions={{ delay: 1000, position: 'bottom' }}
                size="xs"
                label=""
                variant="no-border"
              />
            </>
          )}
          {!isPresentationMode && !autoUpdateDisabled && autoUpdate.allowed() && <AutoUpdate />}
          {!isPresentationMode && rangeEditingEnabled && !isInTopic && (
            <Button
              onClick={displayRangeActions.copyToAllWorksheets}
              testId="rangeSelectorAllWorksheets"
              extraClassNames="ml5"
              icon="fc-copy-date tw:text-sq-theme-dark"
              tooltip={t('RANGE_SELECTOR.COPY_TO_ALL_WORKSHEETS')}
              tooltipOptions={{ delay: 500, position: 'bottom' }}
              size="xs"
              label=""
              variant="no-border"
            />
          )}
        </div>
      </div>
      <div className="flexRowContainer">
        {formMode && (
          <label
            className={clsx('font-size-smaller mr10', {
              disabled: !rangeEditingEnabled,
            })}>
            {t('END')}
          </label>
        )}
        <div
          className={clsx('flexColumnContainer displayRangeDateTime displayRangeEnd', {
            'min-width-165': !formMode && !isInTopic && !isPresentationMode,
          })}
          id="displayRangeEnd">
          <DateTimeEntry
            testId="specDisplayRangeEnd"
            extraClassName={clsx('small flexFill', {
              'min-width-145': !isInTopic,
              'pl26': isInTopic,
              'tw:text-right': !formMode,
              'mr15': !formMode && !isPresentationMode,
              'mr5': isPresentationMode,
              'readOnly': !rangeEditingEnabled,
            })}
            datePickerExtraClassNames="fs11"
            date={displayRange.end}
            updateDate={displayRangeActions.updateEnd}
            // @ts-ignore updateBothDates expects the callback's option to be a string but `displayRangeActions.updateTimes` wants a PushOption.
            updateBothDates={displayRangeActions.updateTimes}
            timezone={timezone}
            otherDate={displayRange.start}
            readOnly={!rangeEditingEnabled}
            timeZoneReadOnly={!timeZoneEditingEnabled}
            trackCategory={trackCategory}
            trackAction={trackAction}
            trackInformation="'End Date set'"
            copyButtonLocation={CopyButtonLocation.Left}
          />
        </div>
      </div>
    </div>
  );
};
